"use client"

/** Shared client-side error de-minification used by EVERY error surface in the
 *  app: the (app) layout boundary (AppErrorBoundary), app/(app)/error.tsx,
 *  app/global-error.tsx, and the agent editor's AgentShellErrorBoundary.
 *
 *  In production React replaces invariant messages with a numeric code
 *  ("Minified React error #185; visit https://react.dev/errors/185 …") and
 *  every stack frame points at a minified chunk (`o`, `rv`, `eB`). On their
 *  own these are unactionable — which is exactly the "minified everywhere"
 *  problem this module fixes. We:
 *    1. decode the React error code into human-readable text, and
 *    2. resolve each stack frame against its deployed `.map` file
 *       (`productionBrowserSourceMaps: true` in next.config.ts ships them).
 *
 *  This logic was originally inline in AgentShellErrorBoundary (agent editor
 *  only); it now lives here so it can be reused by the app-wide boundaries.
 *  See `lib/debug/renderTrace.ts` for the render-trace snapshot that names the
 *  looping component on React #185. */

export type StackFrame = {
    raw: string
    url: string | null
    line: number | null
    column: number | null
}

export type ResolvedFrame = StackFrame & {
    source: string | null
    sourceLine: number | null
    sourceColumn: number | null
    name: string | null
    error?: string
}

export type RenderTraceSnapshot = { renders: Record<string, number>; effects: Record<string, number> }

/** Parse a single stack-trace line. Handles V8 (`at fn (url:l:c)`),
 *  componentStack (`at fn (url:l:c)` or `at div (<anonymous>)`), and the
 *  Safari/Firefox forms (`fn@url:l:c`). Returns a frame with url:null for
 *  lines without a mappable URL (anonymous tags). */
export function parseFrame(line: string): StackFrame | null {
    const raw = line.trim()
    if (!raw) return null
    // V8: `at fnName (https://host/path.js?dpl=…:LINE:COL)`
    let m = raw.match(/\(([^)]+):(\d+):(\d+)\)\s*$/)
    if (m) return { raw, url: m[1], line: parseInt(m[2], 10), column: parseInt(m[3], 10) }
    // V8 anonymous: `at https://host/path.js:LINE:COL`
    m = raw.match(/at\s+(https?:\/\/[^\s]+):(\d+):(\d+)\s*$/)
    if (m) return { raw, url: m[1], line: parseInt(m[2], 10), column: parseInt(m[3], 10) }
    // Component stack URL form: `at https://host/path.js?dpl=…:LINE:COL`
    m = raw.match(/at\s+(https?:\/\/[^\s)]+):(\d+):(\d+)\s*$/)
    if (m) return { raw, url: m[1], line: parseInt(m[2], 10), column: parseInt(m[3], 10) }
    // Firefox/Safari: `fnName@url:l:c`
    m = raw.match(/@(https?:\/\/[^\s]+):(\d+):(\d+)\s*$/)
    if (m) return { raw, url: m[1], line: parseInt(m[2], 10), column: parseInt(m[3], 10) }
    return { raw, url: null, line: null, column: null }
}

const mapCache = new Map<string, Promise<unknown | null>>()

/** Fetch and decode the .map alongside the chunk URL. Strips query string
 *  (Vercel adds `?dpl=…` cache-busters) before appending `.map`. Caches per
 *  chunk URL so we don't re-fetch when the same chunk appears repeatedly. */
async function loadMap(chunkUrl: string): Promise<unknown | null> {
    const base = chunkUrl.split("?")[0]
    if (!base) return null
    if (mapCache.has(base)) return mapCache.get(base)!
    const promise = (async () => {
        try {
            const res = await fetch(`${base}.map`, { credentials: "omit" })
            if (!res.ok) return null
            return await res.json()
        } catch {
            return null
        }
    })()
    mapCache.set(base, promise)
    return promise
}

/** Lazy import the decoder so it's only loaded after a crash — stays out of
 *  the hot-path bundle. */
async function getTraceMapping(): Promise<typeof import("@jridgewell/trace-mapping") | null> {
    try {
        return await import("@jridgewell/trace-mapping")
    } catch {
        return null
    }
}

/** Walk a multi-line stack string and resolve each frame against its chunk's
 *  source map. Frames whose URL we can't recognize pass through with
 *  source:null (we still render their raw text). */
export async function resolveStack(stack: string): Promise<ResolvedFrame[]> {
    const tm = await getTraceMapping()
    if (!tm) {
        return stack.split("\n").map(l => ({
            raw: l, url: null, line: null, column: null,
            source: null, sourceLine: null, sourceColumn: null, name: null,
            error: "trace-mapping not available",
        }))
    }
    const frames = stack.split("\n").map(parseFrame).filter((f): f is StackFrame => f !== null)
    return Promise.all(frames.map(async f => {
        if (!f.url || f.line == null || f.column == null) {
            return { ...f, source: null, sourceLine: null, sourceColumn: null, name: null }
        }
        try {
            const raw = await loadMap(f.url)
            if (!raw) {
                return { ...f, source: null, sourceLine: null, sourceColumn: null, name: null, error: "map fetch failed" }
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const tracer = new tm.TraceMap(raw as any)
            const pos = tm.originalPositionFor(tracer, { line: f.line, column: f.column })
            return {
                ...f,
                source: pos.source ?? null,
                sourceLine: pos.line ?? null,
                sourceColumn: pos.column ?? null,
                name: pos.name ?? null,
            }
        } catch (err) {
            return {
                ...f,
                source: null, sourceLine: null, sourceColumn: null, name: null,
                error: err instanceof Error ? err.message : String(err),
            }
        }
    }))
}

/** Format a resolved frame as one tidy line. Falls back to the raw frame when
 *  no mapping was available so the user always sees something. */
export function formatResolved(f: ResolvedFrame): string {
    if (!f.source) return f.raw
    const fn = f.name ? `${f.name} ` : ""
    return `${fn}at ${f.source}:${f.sourceLine}:${f.sourceColumn ?? 0}`
}

/** React's production build replaces invariant messages with a numeric code.
 *  This maps the codes we actually hit into readable text so the user sees
 *  WHAT broke instead of a cross-reference. #185 doubles as a triage hint.
 *  Unknown codes keep the react.dev link. */
const REACT_ERROR_CODES: Record<string, string> = {
    "185": "Maximum update depth exceeded — a component keeps scheduling a state update that never settles. Usual causes: a setState inside an effect or ref-callback whose dependency (or the callback's own identity) changes on every render, or two effects updating each other's state.",
    "130": "Element type is invalid — a component rendered as undefined (often a bad/circular import or a default-vs-named export mismatch).",
    "31": "Objects are not valid as a React child.",
    "61": "A state update was scheduled on a component that hasn't mounted yet.",
    "300": "Rendered more hooks than during the previous render.",
    "310": "Rendered fewer hooks than expected — a hook was called conditionally or after an early return.",
    "321": "Invalid hook call — hooks can only be called inside the body of a function component.",
    "418": "Hydration failed because the server-rendered HTML didn't match the client.",
    "419": "The server could not finish this Suspense boundary; the client switched to client rendering.",
    "423": "Hydration error — the app switched to client rendering because of a mismatch.",
    "425": "Text content does not match server-rendered HTML.",
}

/** Turn a (possibly minified) error message into something a human can read.
 *  Non-React messages pass through unchanged. */
export function decodeReactErrorMessage(message: string | null | undefined): string {
    if (!message) return "An unexpected error occurred"
    const m = message.match(/Minified React error #(\d+)/)
    if (!m) return message
    const code = m[1]
    const known = REACT_ERROR_CODES[code]
    return known
        ? `React error #${code}: ${known}`
        : `React error #${code} — see https://react.dev/errors/${code}`
}

/** Format a render-trace snapshot as a two-column table, highest counts first,
 *  so the looping component is at the top regardless of how many entries
 *  exist. (For React #185 the top row is the culprit.) */
export function formatRenderTraceSnapshot(snap: RenderTraceSnapshot): string {
    const rows: Array<{ label: string; kind: string; count: number }> = []
    // Null-coalesce both maps: this runs DURING render in the error fallback,
    // and a fallback that throws escapes the boundary entirely (a boundary
    // can't catch its own fallback). Tolerate a malformed/partial snapshot.
    for (const [label, count] of Object.entries(snap?.renders ?? {})) rows.push({ label, kind: "render", count })
    for (const [label, count] of Object.entries(snap?.effects ?? {})) rows.push({ label, kind: "effect", count })
    rows.sort((a, b) => b.count - a.count)
    if (rows.length === 0) return "(no instrumented components rendered)"
    return rows
        .map(r => `  ${String(r.count).padStart(6)} ${r.kind.padEnd(6)} ${r.label}`)
        .join("\n")
}
